import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { getTimelineByDossier, addTimelineEvent } from '../../../services/notaryWorkspaceService';
import { subscribePooled } from '../../../utils/realtimePoolManager';

const formatDateTime = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const EVENT_CONFIG = {
  status_change: { icon: 'RefreshCw', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  document_uploaded: { icon: 'Upload', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  document_approved: { icon: 'CheckCircle2', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  document_rejected: { icon: 'XCircle', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  annotation_added: { icon: 'MessageSquare', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  consultation_scheduled: { icon: 'Calendar', color: 'text-brand-earth', bg: 'bg-brand-gold/10', border: 'border-brand-gold/30' },
  note_added: { icon: 'StickyNote', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
};

const ROLE_LABELS = {
  notaire: 'Notaire',
  acheteur: 'Acheteur',
  vendeur: 'Vendeur',
  admin: 'Admin',
};

const PHASE_STEPS = [
  { key: 'nouveau', label: 'Soumission', sublabel: 'Dossier créé', icon: 'FilePlus', color: 'bg-slate-400' },
  { key: 'instruction_en_cours', label: 'Revue', sublabel: 'Vérification', icon: 'Search', color: 'bg-blue-500' },
  { key: 'dossier_valide', label: 'Validation', sublabel: 'Validé', icon: 'ShieldCheck', color: 'bg-green-500' },
  { key: 'projet_acte_depose', label: 'Acte', sublabel: 'Déposé', icon: 'FileCheck', color: 'bg-purple-500' },
  { key: 'transaction_cloturee', label: 'Clôture', sublabel: 'Finalisée', icon: 'CheckSquare', color: 'bg-brand-earth' },
];

const STATUS_ORDER = ['nouveau', 'instruction_en_cours', 'dossier_valide', 'projet_acte_depose', 'transaction_cloturee'];

const TransactionProgressTracker = ({ dossier, isNotaire = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const currentIndex = STATUS_ORDER?.indexOf(dossier?.status);

  const loadTimeline = useCallback(async () => {
    if (!dossier?.id) return;
    setLoading(true);
    const { data } = await getTimelineByDossier(dossier?.id);
    setEvents(data || []);
    setLoading(false);
  }, [dossier?.id]);

  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  useEffect(() => {
    if (!dossier?.id) return;
    const unsubscribe = subscribePooled({
      channelName: `timeline-${dossier?.id}`,
      listeners: [
        { event: 'INSERT', schema: 'public', table: 'transaction_timeline_events', filter: `dossier_id=eq.${dossier?.id}` },
      ],
      debounceMs: 300,
      callback: () => loadTimeline(),
    });
    return () => unsubscribe?.();
  }, [dossier?.id, loadTimeline]);

  const handleAddNote = async () => {
    if (!noteText?.trim()) return;
    setAddingNote(true);
    await addTimelineEvent(dossier?.id, 'note_added', 'Note ajoutée', noteText, isNotaire ? 'notaire' : 'admin');
    setNoteText('');
    setShowAddNote(false);
    setAddingNote(false);
    await loadTimeline();
  };

  return (
    <div className="space-y-5">
      {/* Phase progress */}
      <div className="bg-white rounded-2xl border border-border/50 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-ios-callout font-system font-body-semibold text-text-primary flex items-center space-x-2">
            <Icon name="GitBranch" size={16} className="text-brand-earth" />
            <span>Phases de la transaction</span>
          </h3>
          <span className="text-[10px] font-system font-body-semibold text-text-secondary bg-slate-100 px-2 py-1 rounded-full">
            {Math.min(currentIndex + 1, PHASE_STEPS?.length)}/{PHASE_STEPS?.length}
          </span>
        </div>

        {/* Desktop horizontal stepper */}
        <div className="hidden sm:flex items-center">
          {PHASE_STEPS?.map((step, idx) => {
            const isDone = idx < currentIndex;
            const isCurrent = idx === currentIndex;
            const isPending = idx > currentIndex;
            return (
              <React.Fragment key={step?.key}>
                <div className={`flex flex-col items-center flex-1 min-w-0 transition-all ${isPending ? 'opacity-35' : 'opacity-100'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                    isCurrent ? `${step?.color} text-white shadow-lg ring-4 ring-offset-2 ring-current/20` :
                    isDone ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <Icon name={isDone ? 'Check' : step?.icon} size={16} />
                  </div>
                  <p className={`text-[11px] font-system font-body-semibold text-center leading-tight ${isCurrent ? 'text-brand-earth' : isDone ? 'text-green-600' : 'text-text-secondary'}`}>
                    {step?.label}
                  </p>
                  <p className="text-[10px] text-text-secondary font-system text-center leading-tight mt-0.5">{step?.sublabel}</p>
                </div>
                {idx < PHASE_STEPS?.length - 1 && (
                  <div className={`h-0.5 flex-1 max-w-8 rounded-full mx-1 transition-all ${idx < currentIndex ? 'bg-green-400' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Mobile: horizontal scrollable stepper with larger tap areas */}
        <div className="sm:hidden">
          {/* Progress bar */}
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
            <div
              className="h-full bg-gradient-to-r from-brand-gold to-brand-earth rounded-full transition-all duration-500"
              style={{ width: `${Math.max(0, (currentIndex / (PHASE_STEPS?.length - 1)) * 100)}%` }}
            />
          </div>
          {/* Scrollable step pills */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {PHASE_STEPS?.map((step, idx) => {
              const isDone = idx < currentIndex;
              const isCurrent = idx === currentIndex;
              const isPending = idx > currentIndex;
              return (
                <div key={step?.key}
                  className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2.5 rounded-xl border transition-all ${
                    isCurrent ? `${step?.color} text-white border-transparent shadow-md` :
                    isDone ? 'bg-green-50 border-green-200 text-green-700' : 'bg-slate-50 border-border/50 text-text-secondary opacity-50'
                  }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCurrent ? 'bg-white/20' : isDone ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    <Icon name={isDone ? 'Check' : step?.icon} size={13} className={isCurrent ? 'text-white' : isDone ? 'text-green-600' : 'text-slate-400'} />
                  </div>
                  <div>
                    <p className={`text-[11px] font-system font-body-semibold leading-tight ${isCurrent ? 'text-white' : ''}`}>{step?.label}</p>
                    <p className={`text-[10px] leading-tight ${isCurrent ? 'text-white/80' : 'text-text-secondary'}`}>{step?.sublabel}</p>
                  </div>
                  {isCurrent && (
                    <span className="text-[9px] font-system font-body-semibold bg-white/20 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">En cours</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Timeline events */}
      <div className="bg-white rounded-2xl border border-border/50 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-ios-callout font-system font-body-semibold text-text-primary flex items-center space-x-2">
            <Icon name="Activity" size={16} className="text-brand-earth" />
            <span>Historique en temps réel</span>
          </h3>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] text-green-600 font-system font-body-semibold hidden sm:inline">En direct</span>
            {isNotaire && (
              <button onClick={() => setShowAddNote(v => !v)}
                className="flex items-center space-x-1 px-3 py-2 bg-brand-gold/10 text-brand-earth rounded-lg text-[11px] font-system font-body-semibold hover:bg-brand-gold/20 active:bg-brand-gold/30 transition-colors touch-manipulation">
                <Icon name="Plus" size={13} />
                <span>Note</span>
              </button>
            )}
          </div>
        </div>

        {showAddNote && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e?.target?.value)}
              placeholder="Ajouter une note visible par toutes les parties..."
              rows={3}
              className="w-full px-3 py-3 border border-amber-300 rounded-xl text-ios-callout font-system focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none bg-white"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowAddNote(false)}
                className="flex-1 py-3 border border-amber-300 rounded-xl text-ios-callout font-system text-amber-700 hover:bg-amber-100 active:bg-amber-200 transition-colors touch-manipulation">
                Annuler
              </button>
              <button onClick={handleAddNote} disabled={addingNote || !noteText?.trim()}
                className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-amber-600 active:bg-amber-700 disabled:opacity-60 transition-colors touch-manipulation">
                {addingNote ? 'Ajout...' : 'Publier'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3]?.map(i => <div key={i} className="flex space-x-3 animate-pulse"><div className="w-8 h-8 bg-slate-100 rounded-full flex-shrink-0" /><div className="flex-1 space-y-1.5"><div className="h-3 bg-slate-100 rounded w-2/3" /><div className="h-2.5 bg-slate-100 rounded w-1/2" /></div></div>)}
          </div>
        ) : events?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Clock" size={24} className="text-text-secondary mx-auto mb-2" />
            <p className="text-ios-callout font-system text-text-secondary">Aucun événement enregistré</p>
            <p className="text-ios-caption1 text-text-secondary mt-1">Les mises à jour apparaîtront ici en temps réel.</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border/50" />
            <div className="space-y-4">
              {events?.map((event) => {
                const conf = EVENT_CONFIG?.[event?.event_type] || EVENT_CONFIG?.note_added;
                return (
                  <div key={event?.id} className="flex items-start space-x-3 relative pl-2">
                    <div className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center flex-shrink-0 z-10 ${conf?.bg} shadow-sm`}>
                      <Icon name={conf?.icon} size={14} className={conf?.color} />
                    </div>
                    <div className={`flex-1 min-w-0 p-3 rounded-xl border ${conf?.bg} ${conf?.border}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className={`text-ios-callout font-system font-body-semibold ${conf?.color}`}>{event?.event_title}</p>
                        <span className="text-[10px] text-text-secondary font-system flex-shrink-0">{formatDateTime(event?.created_at)}</span>
                      </div>
                      {event?.event_description && (
                        <p className="text-ios-caption1 text-text-secondary mt-1 font-system">{event?.event_description}</p>
                      )}
                      {event?.triggered_by_role && (
                        <span className="inline-block mt-1.5 text-[10px] font-system font-body-semibold text-text-secondary bg-white/60 px-1.5 py-0.5 rounded-full border border-white/80">
                          {ROLE_LABELS?.[event?.triggered_by_role] || event?.triggered_by_role}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionProgressTracker;
